// PATH: src/components/catalog/MissionZeroDialog.tsx
/**
 * Mission Zéro d'un niveau : les items du test de positionnement lus par
 * l'app élève (tc_learn). Le serveur stocke le JSON tel quel (JSONB) : soit
 * l'ancien tableau plat d'items, soit l'enveloppe { age_range, items[],
 * scoring_notes } — envoyée entière, tc_chat_be en lit tous les champs.
 */
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import { useMissionZeroApi } from "../../api";
import type { Grade } from "../../models/domain";
import { tokens } from "../../theme/theme";
import { TemplateDownload, templateUrl } from "../ui/TemplateDownload";
import { parseMissionZeroJson } from "./missionZero";
import { EmptyState, StatusPill, errorMessage, useToast, type PillTone } from "../ui";

interface MZItem {
  item_id?: string;
  block?: string;
  render_type?: string;
  prompt?: string;
}

const toItems = (raw: unknown): MZItem[] => {
  if (Array.isArray(raw)) return raw as MZItem[];
  const items = (raw as { items?: unknown })?.items;
  return Array.isArray(items) ? (items as MZItem[]) : [];
};

const countBy = <T,>(arr: T[], key: (x: T) => string) =>
  arr.reduce<Record<string, number>>((acc, x) => {
    const k = key(x) || "?";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

const formatDate = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/** Blocs CHC du test, dans l'ordre. */
const BLOCKS: Array<[string, PillTone]> = [
  ["Gf", "primary"],
  ["Gc", "info"],
  ["Gv", "success"],
  ["Gwm", "warning"],
  ["Gs", "neutral"],
];
const toneOf = (b?: string): PillTone => BLOCKS.find(([k]) => k === b)?.[1] ?? "neutral";

interface Props {
  open: boolean;
  grade: Grade | null;
  onClose: () => void;
}

export const MissionZeroDialog: React.FC<Props> = ({ open, grade, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { getMissionZeroItems, putMissionZeroItems } = useMissionZeroApi();
  const api = React.useRef({ getMissionZeroItems, putMissionZeroItems });
  api.current = { getMissionZeroItems, putMissionZeroItems };
  const toast = useToast();

  const [tab, setTab] = React.useState<"current" | "replace">("current");
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [entry, setEntry] = React.useState<{
    raw: unknown;
    items: MZItem[];
    version?: number;
    updatedAt?: string;
  } | null>(null);
  const [text, setText] = React.useState("");
  const fileRef = React.useRef<HTMLInputElement>(null);
  const loadTemplate = async () => {
    try {
      const res = await fetch(templateUrl("mission-zero-exemple.json"));
      setText(await res.text());
      setSaveError(null);
    } catch {
      setSaveError("Modèle introuvable.");
    }
  };
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const load = React.useCallback(async (gradeId: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.current.getMissionZeroItems(gradeId);
      setEntry({ raw: data.items, items: toItems(data.items), version: data.version, updatedAt: data.updated_at });
    } catch (e) {
      setEntry(null);
      if ((e as { response?: { status?: number } })?.response?.status !== 404)
        setLoadError(errorMessage(e, "Impossible de charger la Mission Zéro."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open || !grade) return;
    setTab("current");
    setText("");
    setSaveError(null);
    void load(grade.id);
  }, [open, grade, load]);

  const parsed = text.trim() ? parseMissionZeroJson(text) : null;

  const save = async () => {
    if (!grade || !parsed || "error" in parsed) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.current.putMissionZeroItems(grade.id, parsed.payload);
      toast.success(`Mission Zéro enregistrée (${parsed.count} items).`);
      setText("");
      await load(grade.id);
      setTab("current");
    } catch (e) {
      setSaveError(errorMessage(e, "Échec de l'enregistrement."));
    } finally {
      setSaving(false);
    }
  };

  const download = () => {
    if (!entry) return;
    const blob = new Blob([JSON.stringify(entry.raw, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mission-zero-${grade?.code ?? "niveau"}-v${entry.version ?? 1}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = entry?.items ?? [];
  const blocks = countBy(items, (it) => it.block ?? "?");
  const renderTypes = countBy(items, (it) => it.render_type ?? "?");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
          <span>Mission Zéro — {grade?.name}</span>
          {!loading && (
            <StatusPill tone={items.length ? "success" : "neutral"}>
              {items.length ? `${items.length} items` : "non configurée"}
            </StatusPill>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
          Items du test lus par l'application élève pour ce niveau.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: "4px !important" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tab value="current" label="Contenu actuel" />
          <Tab value="replace" label="Remplacer par un JSON" />
        </Tabs>

        {tab === "current" &&
          (loading ? (
            <Stack spacing={1}>
              <Skeleton height={28} width="40%" />
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={34} />
              ))}
            </Stack>
          ) : loadError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => grade && void load(grade.id)}>
                  Réessayer
                </Button>
              }
            >
              {loadError}
            </Alert>
          ) : items.length === 0 ? (
            <EmptyState
              dense
              title="Aucun item pour ce niveau"
              description="Collez le JSON de la Mission Zéro dans l'onglet « Remplacer par un JSON »."
              action={
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <Button variant="outlined" onClick={() => setTab("replace")}>
                    Importer un JSON
                  </Button>
                  <TemplateDownload file="mission-zero-exemple.json">Modèle</TemplateDownload>
                </Stack>
              }
            />
          ) : (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Version {entry?.version ?? "?"} · mise à jour le {formatDate(entry?.updatedAt)}
                </Typography>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={download}>
                  Télécharger le JSON
                </Button>
              </Stack>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Blocs
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                  {BLOCKS.map(([b, tone]) => (
                    <StatusPill key={b} tone={blocks[b] ? tone : "neutral"} dot={false}>
                      {b} · {blocks[b] ?? 0}
                    </StatusPill>
                  ))}
                  {Object.keys(blocks)
                    .filter((b) => !BLOCKS.some(([k]) => k === b))
                    .map((b) => (
                      <StatusPill key={b} dot={false}>
                        {b} · {blocks[b]}
                      </StatusPill>
                    ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Types de rendu
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                  {Object.entries(renderTypes).map(([rt, n]) => (
                    <StatusPill key={rt} dot={false}>
                      <span className="mono">{rt}</span>&nbsp;· {n}
                    </StatusPill>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: 2, overflow: "hidden" }}>
                {items.map((it, i) => (
                  <Stack
                    key={it.item_id ?? i}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ px: 1.5, py: 0.75, borderTop: i ? `1px solid ${tokens.border}` : "none" }}
                  >
                    <Box sx={{ width: 52, flexShrink: 0 }}>
                      <StatusPill tone={toneOf(it.block)} dot={false}>
                        {it.block ?? "?"}
                      </StatusPill>
                    </Box>
                    <Typography
                      variant="caption"
                      className="mono"
                      color="text.secondary"
                      sx={{ width: 120, flexShrink: 0 }}
                      noWrap
                    >
                      {it.render_type ?? "?"}
                    </Typography>
                    <Typography variant="body2" noWrap dir="auto" sx={{ flex: 1, minWidth: 0 }}>
                      {it.prompt || it.item_id || `item ${i + 1}`}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Stack>
          ))}

        {tab === "replace" && (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Le JSON remplace entièrement la version actuelle. Accepté : un tableau d'items, ou l'objet complet
              {" { age_range, items, scoring_notes }"}. Chaque item a au moins <span className="mono">item_id</span>,{" "}
              <span className="mono">block</span> et <span className="mono">render_type</span>.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileRef.current?.click()}
              >
                Ouvrir un fichier .json
              </Button>
              <TemplateDownload file="mission-zero-exemple.json">Télécharger le modèle</TemplateDownload>
              <Button size="small" onClick={() => void loadTemplate()}>
                Partir du modèle
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f)
                    void f.text().then((t) => {
                      setText(t);
                      setSaveError(null);
                    });
                }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Le modèle reprend une vraie Mission Zéro (un item par type d'exercice, blocs Gf, Gc, Gv, Gwm, Gs) : il est
              accepté tel quel.
            </Typography>
            <TextField
              multiline
              minRows={12}
              maxRows={22}
              fullWidth
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setSaveError(null);
              }}
              placeholder={
                '{\n  "age_range": "6-7",\n  "items": [\n    { "item_id": "gf_001", "block": "Gf", "render_type": "mcq_text", "prompt": "…" }\n  ],\n  "scoring_notes": {}\n}'
              }
              slotProps={{ htmlInput: { className: "mono", spellCheck: false } }}
              error={Boolean(parsed && "error" in parsed)}
              helperText={parsed && "error" in parsed ? parsed.error : " "}
            />
            {parsed && !("error" in parsed) && (
              <Alert severity="success">{parsed.count} items prêts à être enregistrés.</Alert>
            )}
            {saveError && <Alert severity="error">{saveError}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Fermer
        </Button>
        {tab === "replace" && (
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={saving || !parsed || "error" in parsed}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Enregistrer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MissionZeroDialog;
